import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  db,
  collection,
  query,
  orderBy,
  limit,
  startAfter,
} from "../firebase";
import { doc, deleteDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  ShoppingCart,
  DollarSign,
  RotateCcw,
  Search,
  Calendar,
  Eye,
  Trash2,
  X,
} from "lucide-react";

/* =========================
   HELPERS
========================= */

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const parseMs = (v) => {
  if (typeof v === "number") return v;
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
};

const fmtDate = (d) => new Date(d).toISOString().slice(0, 10);

const fmtDateLabel = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const fmtDateTime = (isoOrMs) => {
  const d = typeof isoOrMs === "number" ? new Date(isoOrMs) : new Date(isoOrMs);
  return d.toLocaleString("es-AR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const toNum = (n) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
};

const peso = (n) =>
  `$${Number(n || 0).toLocaleString("es-AR", {
    maximumFractionDigits: 0,
  })}`;

const formatCompactPeso = (n) => {
  const num = Number(n || 0);
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(0)}k`;
  return peso(num);
};

const compare = (a, b, k, dir) => {
  const ra = a?.[k] ?? "";
  const rb = b?.[k] ?? "";
  const na = parseFloat(ra);
  const nb = parseFloat(rb);

  if (!isNaN(na) && !isNaN(nb)) return dir === "asc" ? na - nb : nb - na;

  return dir === "asc"
    ? ra.toString().localeCompare(rb.toString())
    : rb.toString().localeCompare(ra.toString());
};

const formatDuration = (ms) => {
  if (!ms || ms < 0) return "—";
  const h = Math.floor(ms / 3600000);
  const m = Math.round((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
};

const useCountUp = (value, duration = 800) => {
  const [display, setDisplay] = useState(value || 0);
  const prev = useRef(value || 0);

  useEffect(() => {
    if (prev.current === (value || 0)) return;
    prev.current = value || 0;

    let current = 0;
    const step = Math.ceil(((value || 0) / (duration / 16)) || 0);

    const id = setInterval(() => {
      current += step;
      if (current >= (value || 0)) {
        current = value || 0;
        clearInterval(id);
      }
      setDisplay(current);
    }, 16);

    return () => clearInterval(id);
  }, [value, duration]);

  return display;
};

/* =========================
   UI
========================= */

const DashboardCard = ({ children, className = "" }) => (
  <div
    className={`rounded-[28px] bg-[#f8f8fb] border border-[#e8e8ee] shadow-[0_4px_20px_rgba(15,23,42,0.06)] ${className}`}
  >
    {children}
  </div>
);

const KPI = ({ title, value, subtitle, icon: Icon, trend, trendColor = "green" }) => {
  const trendClass =
    trendColor === "red" ? "text-red-500" : "text-emerald-500";

  return (
    <DashboardCard className="p-7 min-h-[150px]">
      <div className="flex items-start justify-between mb-5">
        <span className="text-[15px] text-[#1f1f2e] font-medium">{title}</span>
        {Icon ? (
          <div className="w-11 h-11 rounded-2xl bg-[#fff4cc] flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#e2b300]" />
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[28px] leading-none font-bold text-[#111322]">
          {value}
        </span>

        {subtitle ? (
          <span className="text-[14px] text-[#7b8194]">{subtitle}</span>
        ) : null}

        {trend ? (
          <span className={`text-[14px] font-semibold mt-1 ${trendClass}`}>
            {trend}
          </span>
        ) : null}
      </div>
    </DashboardCard>
  );
};

const SectionTitle = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-4">
    <div>
      <h3 className="text-[20px] md:text-[26px] leading-none font-bold text-[#111322]">
        {title}
      </h3>
      {subtitle ? (
        <p className="text-[12px] uppercase tracking-[0.12em] text-[#8a8fa1] mt-2 font-semibold">
          {subtitle}
        </p>
      ) : null}
    </div>
    {right}
  </div>
);

const BadgeRecovery = ({ cmp, amount }) => {
  if (!cmp) {
    return (
      <span className="inline-flex px-3 py-1.5 rounded-full bg-[#ececf1] text-[#5f6474] text-xs font-semibold">
        —
      </span>
    );
  }

  const styles =
    cmp === "eq"
      ? "bg-sky-100 text-sky-700"
      : cmp === "gt"
      ? "bg-emerald-100 text-emerald-700"
      : "bg-amber-100 text-amber-700";

  const label =
    cmp === "eq" ? "Igual" : cmp === "gt" ? "Mayor" : "Menor";

  return (
    <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-semibold ${styles}`}>
      {label} · {peso(amount)}
    </span>
  );
};

/* =========================
   SCREEN
========================= */

export default function AbandonedCartsScreen() {
  const [carts, setCarts] = useState([]);
  const [initialCarts, setInitialCarts] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", dir: "desc" });

  const now = new Date();
  const d30 = new Date(now.getTime() - 30 * DAY_MS);
  const [fromDate, setFromDate] = useState(fmtDate(d30));
  const [toDate, setToDate] = useState(fmtDate(now));

  const attributionDays = 7;

  const [showModal, setShowModal] = useState(false);
  const [selectedCart, setSelectedCart] = useState(null);

  const mapCartDoc = (docSnap) => {
    const data = docSnap.data();

    const recoveredAmountConEnvio = toNum(
      data.recoveredAmountConEnvio ?? data.totalconenvio
    );
    const recoveredAmountProducts = toNum(data.recoveredAmountProducts);
    const total = toNum(data.total);
    const _createdMs = data.createdAtMs ?? parseMs(data.created_at);
    const recoveredVentas = Array.isArray(data.recoveredVentas) ? data.recoveredVentas : [];

    return {
      id: docSnap.id,
      ...data,
      total,
      _createdMs,
      recoveredVentas,
      recoveredAmountConEnvio,
      recoveredAmountProducts,
    };
  };

  useEffect(() => {
    fetchInitialCarts();
  }, []);

  const fetchInitialCarts = async () => {
    try {
      setLoading(true);
      const { getDocs } = await import("firebase/firestore");

      const q = query(
        collection(db, "abandonedCarts"),
        orderBy("created_at", "desc"),
        limit(2000)
      );

      const snap = await getDocs(q);
      const arr = snap.docs.map(mapCartDoc);

      setCarts(arr);
      setInitialCarts(arr);
      setLastDoc(snap.docs[snap.docs.length - 1] ?? null);
      setHasMore(snap.size === 2000);
    } catch (e) {
      console.error(e);
      toast.error("Error al cargar carritos");
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreCarts = async () => {
    if (!lastDoc) return;

    try {
      setLoading(true);
      const { getDocs } = await import("firebase/firestore");

      const q = query(
        collection(db, "abandonedCarts"),
        orderBy("created_at", "desc"),
        startAfter(lastDoc),
        limit(2000)
      );

      const snap = await getDocs(q);
      const arr = snap.docs.map(mapCartDoc);

      setCarts((p) => [...p, ...arr]);
      setInitialCarts((p) => [...p, ...arr]);
      setLastDoc(snap.docs[snap.docs.length - 1] ?? null);
      setHasMore(snap.size === 2000);
    } catch (e) {
      console.error(e);
      toast.error("Error al cargar más carritos");
    } finally {
      setLoading(false);
    }
  };

  const filteredBySearch = useMemo(() => {
    const t = searchTerm.toLowerCase().trim();
    if (!t) return carts;

    return carts.filter(
      (c) =>
        c.contact_name?.toLowerCase().includes(t) ||
        c.contact_phone?.toLowerCase().includes(t) ||
        c.contact_identification?.toLowerCase().includes(t) ||
        String(c.id).includes(t)
    );
  }, [carts, searchTerm]);

  const filteredByDate = useMemo(() => {
    const fromMs = new Date(fromDate + "T00:00:00").getTime();
    const toMs = new Date(toDate + "T23:59:59").getTime();

    return filteredBySearch.filter((c) => {
      const ms = c._createdMs;
      return ms >= fromMs && ms <= toMs;
    });
  }, [filteredBySearch, fromDate, toDate]);

  const filteredCarts = useMemo(
    () => [...filteredByDate].sort((a, b) => compare(a, b, sortConfig.key, sortConfig.dir)),
    [filteredByDate, sortConfig]
  );

  const matching = useMemo(() => {
    const map = new Map();

    for (const c of filteredByDate) {
      const ventas = Array.isArray(c.recoveredVentas) ? c.recoveredVentas : [];

      if (ventas.length) {
        const amountFromVentas = ventas.reduce((s, v) => {
          const m = toNum(v.total_paid) || toNum(v.total);
          return s + m;
        }, 0);

        const amount = toNum(
          c.recoveredAmountConEnvio || c.recoveredAmountProducts || amountFromVentas
        );

        const cartTotal = toNum(c.total);
        const cmp = amount === cartTotal ? "eq" : amount > cartTotal ? "gt" : "lt";

        const ventasEnriq = ventas.map((v) => ({
          ...v,
          _createdMs: v.createdAtMs ?? parseMs(v.createdAt),
        }));

        map.set(String(c.id), { ventas: ventasEnriq, amount, cmp });
      }
    }

    return map;
  }, [filteredByDate]);

  const calcRecoveredForCarts = (cartsList) => {
    let recCount = 0,
      recAmount = 0;
    let cntEq = 0,
      cntLt = 0,
      cntGt = 0;

    for (const c of cartsList) {
      const ventas = Array.isArray(c.recoveredVentas) ? c.recoveredVentas : [];
      if (!ventas.length && !c.recovered) continue;

      const amountFromVentas = ventas.reduce(
        (s, v) => s + (toNum(v.total_paid) || toNum(v.total)),
        0
      );

      const amount = toNum(
        c.recoveredAmountConEnvio || c.recoveredAmountProducts || amountFromVentas
      );

      if (amount <= 0) continue;

      recCount += 1;
      recAmount += amount;

      const cartTotal = toNum(c.total);

      if (amount === cartTotal) cntEq++;
      else if (amount > cartTotal) cntGt++;
      else cntLt++;
    }

    return {
      recoveredCount: recCount,
      recoveredAmount: recAmount,
      splits: { cntEq, cntLt, cntGt },
    };
  };

  const stats = useMemo(() => {
    const cartsRange = filteredByDate;

    const totalValueRange = cartsRange.reduce((acc, c) => acc + toNum(c.total), 0);
    const recRange = calcRecoveredForCarts(cartsRange);

    return {
      cartsRangeCount: cartsRange.length,
      totalValueRange,
      recoveredRangeCount: recRange.recoveredCount,
      recoveredRangeAmount: recRange.recoveredAmount,
      splitsRange: recRange.splits,
    };
  }, [filteredByDate]);

  const topRecoveredClients = useMemo(() => {
    const map = new Map();

    for (const c of filteredByDate) {
      const hit = matching.get(String(c.id));
      if (!hit) continue;

      const name = c.contact_name || `Cliente ${c.id}`;
      const prev = map.get(name) || 0;
      map.set(name, prev + hit.amount);
    }

    return Array.from(map.entries())
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [filteredByDate, matching]);

  const pieData = useMemo(() => {
    const total = stats.recoveredRangeCount || 1;

    return [
      {
        name: "Igual",
        value: stats.splitsRange.cntEq || 0,
        pct: ((stats.splitsRange.cntEq || 0) / total) * 100,
      },
      {
        name: "Mayor",
        value: stats.splitsRange.cntGt || 0,
        pct: ((stats.splitsRange.cntGt || 0) / total) * 100,
      },
      {
        name: "Menor",
        value: stats.splitsRange.cntLt || 0,
        pct: ((stats.splitsRange.cntLt || 0) / total) * 100,
      },
    ];
  }, [stats]);

  const barData = useMemo(() => {
    return topRecoveredClients.map((item) => ({
      name:
        item.name.length > 14 ? `${item.name.slice(0, 14)}...` : item.name,
      amount: item.amount,
      fullName: item.name,
    }));
  }, [topRecoveredClients]);

  const removeCart = async (id) => {
    if (!window.confirm(`¿Eliminar carrito #${id}?`)) return;

    try {
      await deleteDoc(doc(db, "abandonedCarts", String(id)));
      setCarts((p) => p.filter((c) => c.id !== id));
      toast.success("Carrito eliminado");
    } catch (e) {
      console.error(e);
      toast.error("Error al eliminar");
    }
  };

  const displayedRangeCount = useCountUp(stats.cartsRangeCount);
  const displayedTotalValue = useCountUp(stats.totalValueRange);
  const displayedRecoveredAmount = useCountUp(stats.recoveredRangeAmount);

  const rangeLabel = `${fmtDateLabel(fromDate)} - ${fmtDateLabel(toDate)}`;
  const pieColors = ["#f4c400", "#f0cf42", "#444444"];

  return (
    <div className="min-h-screen bg-[#f4f2ed] p-5 md:p-8 font-poppins">
      <div className="max-w-[1600px] mx-auto flex flex-col gap-8">
        {/* HEADER */}
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-[#fff4cc] flex items-center justify-center shrink-0">
            <ShoppingCart className="w-8 h-8 text-[#e2b300]" />
          </div>

          <div>
            <h1 className="text-[34px] leading-none font-bold text-[#111322]">
              Carritos abandonados
            </h1>
            <p className="text-[#8b8f9b] text-[18px] mt-2">
              Indicadores clave de recuperación
            </p>
          </div>
        </div>

        {/* KPIS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <KPI
            title="Carritos en rango"
            value={displayedRangeCount.toLocaleString("es-AR")}
            subtitle={rangeLabel}
            icon={ShoppingCart}
          />

          <KPI
            title="Valor total"
            value={peso(displayedTotalValue)}
            subtitle={rangeLabel}
            trend={`${stats.cartsRangeCount.toLocaleString("es-AR")} carritos`}
            trendColor="green"
            icon={DollarSign}
          />

          <KPI
            title="Recuperado"
            value={peso(displayedRecoveredAmount)}
            subtitle={rangeLabel}
            trend={`${stats.recoveredRangeCount.toLocaleString("es-AR")} carritos recuperados`}
            trendColor="green"
            icon={RotateCcw}
          />
        </div>

        {/* BLOQUE CENTRAL */}
        <DashboardCard className="overflow-hidden">
          <div className="grid grid-cols-1 xl:grid-cols-12">
            {/* LEFT */}
            <div className="xl:col-span-4 border-b xl:border-b-0 xl:border-r border-[#e6e7ee] p-7">
              <SectionTitle
                title="Top 5 Recuperados"
                subtitle="CONCENTRACIÓN DE RECUPERO"
              />

              <div className="mt-10 flex flex-col gap-7">
                {topRecoveredClients.length ? (
                  topRecoveredClients.map((item, index) => {
                    const max = topRecoveredClients[0]?.amount || 1;
                    const width = `${(item.amount / max) * 100}%`;
                    const colors = [
                      "bg-[#f4c400]",
                      "bg-[#f0ca31]",
                      "bg-[#e3b600]",
                      "bg-[#cfa300]",
                      "bg-[#444444]",
                    ];

                    return (
                      <div key={item.name} className="grid grid-cols-[90px_1fr] gap-4 items-center">
                        <div className="text-[14px] text-[#677084] leading-tight">
                          {item.name}
                        </div>
                        <div className="w-full h-[50px] rounded-xl bg-transparent flex items-center">
                          <div
                            className={`h-[50px] rounded-lg ${colors[index] || "bg-[#f4c400]"}`}
                            style={{ width }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-[#7b8194] mt-8">
                    No hay datos suficientes para mostrar.
                  </p>
                )}
              </div>
            </div>

            {/* CENTER */}
            <div className="xl:col-span-4 border-b xl:border-b-0 xl:border-r border-[#e6e7ee]">
              <div className="p-7">
                <SectionTitle
                  title="Estado Recupero"
                  subtitle="DISTRIBUCIÓN POR TIPO"
                />
              </div>

              <div className="px-7 pb-7 pt-2">
                <div className="h-[290px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={0}
                        outerRadius={90}
                        dataKey="value"
                        stroke="none"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border-t border-[#e6e7ee] p-7">
                <p className="text-[12px] uppercase tracking-[0.12em] text-[#8a8fa1] font-semibold mb-5">
                  Top ranking
                </p>

                <div className="space-y-4">
                  {pieData.map((item, i) => (
                    <div key={item.name} className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: pieColors[i] }}
                        />
                        <span className="text-[16px] text-[#2b3142] truncate">
                          {item.name}
                        </span>
                      </div>

                      <span className="text-[16px] font-bold text-[#2b3142]">
                        {item.pct.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="xl:col-span-4">
              <div className="p-7 border-b border-[#e6e7ee]">
                <div className="grid grid-cols-[1fr_auto_auto] items-start gap-6">
                  <div>
                    <h3 className="text-[24px] leading-none font-bold text-[#111322]">
                      Análisis
                      <br />
                      Detallado
                    </h3>
                    <p className="text-[12px] uppercase tracking-[0.12em] text-[#8a8fa1] font-semibold mt-2">
                      Métricas de recupero
                    </p>
                  </div>

                  <div className="pl-6 border-l border-[#e6e7ee]">
                    <span className="block text-[12px] uppercase text-[#8a8fa1] font-semibold">
                      Recup.
                    </span>
                    <span className="block text-[20px] font-bold text-[#111322] mt-1">
                      {formatCompactPeso(stats.recoveredRangeAmount)}
                    </span>
                  </div>

                  <div className="pl-6 border-l border-[#e6e7ee]">
                    <span className="block text-[12px] uppercase text-[#8a8fa1] font-semibold">
                      Clientes
                    </span>
                    <span className="block text-[20px] font-bold text-[#111322] mt-1">
                      {stats.recoveredRangeCount}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-7">
                <div className="h-[420px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barData} barCategoryGap={18}>
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="4 4"
                        stroke="#dddddf"
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: "#7b8194", fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={(v) => formatCompactPeso(v)}
                        tick={{ fill: "#7b8194", fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        formatter={(value, name, props) => [peso(value), props.payload.fullName]}
                      />
                      <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                        {barData.map((entry, index) => {
                          const colors = ["#f4c400", "#f0ca31", "#e3b600", "#cfa300", "#444444"];
                          return <Cell key={`bar-${index}`} fill={colors[index] || "#f4c400"} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </DashboardCard>

        {/* FILTROS */}
        <div className="flex flex-col md:flex-row gap-4">
          <div>
            <label className="block text-[12px] uppercase tracking-[0.12em] text-[#8a8fa1] font-semibold mb-3">
              Desde
            </label>
            <div className="relative">
              <Calendar className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8fa1]" />
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-14 w-[220px] rounded-2xl border border-[#e1e1ea] bg-[#f8f8fb] pl-11 pr-4 text-[#111322] outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-[12px] uppercase tracking-[0.12em] text-[#8a8fa1] font-semibold mb-3">
              Hasta
            </label>
            <div className="relative">
              <Calendar className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8fa1]" />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-14 w-[220px] rounded-2xl border border-[#e1e1ea] bg-[#f8f8fb] pl-11 pr-4 text-[#111322] outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-[12px] uppercase tracking-[0.12em] text-[#8a8fa1] font-semibold mb-3">
              Buscar
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8fa1]" />
              <input
                type="text"
                placeholder="Cliente, teléfono o ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-14 w-[280px] rounded-2xl border border-[#e1e1ea] bg-[#f8f8fb] pl-11 pr-4 text-[#111322] outline-none"
              />
            </div>
          </div>
        </div>

        {/* TABLA */}
        <DashboardCard className="p-0 overflow-hidden">
          <div className="p-7 border-b border-[#e6e7ee]">
            <div>
              <h3 className="text-[22px] font-bold text-[#111322]">Listado de carritos</h3>
              <p className="text-[#8a8fa1] text-sm mt-1">
                {filteredCarts.length} resultados encontrados
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left border-b border-[#e6e7ee]">
                  {[
                    ["ID", "id"],
                    ["Fecha", "created_at"],
                    ["Cliente", "contact_name"],
                    ["Teléfono", "contact_phone"],
                    ["Total", "total"],
                  ].map(([label, key]) => (
                    <th
                      key={key}
                      onClick={() =>
                        setSortConfig((p) =>
                          p.key === key
                            ? { key, dir: p.dir === "asc" ? "desc" : "asc" }
                            : { key, dir: "asc" }
                        )
                      }
                      className="px-7 py-5 text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold cursor-pointer"
                    >
                      {label} {sortConfig.key === key ? (sortConfig.dir === "asc" ? "▲" : "▼") : ""}
                    </th>
                  ))}
                  <th className="px-7 py-5 text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">
                    Recupero
                  </th>
                  <th className="px-7 py-5 text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredCarts.length ? (
                  filteredCarts.map((cart) => {
                    const hit = matching.get(String(cart.id));
                    const recovered = Boolean(hit);
                    const amount = hit?.amount || 0;
                    const cmp = recovered ? hit.cmp : null;

                    return (
                      <tr
                        key={cart.id}
                        className="border-b border-[#efeff4] hover:bg-[#f3f3f8] transition"
                      >
                        <td className="px-7 py-5 text-[14px] font-semibold text-[#111322]">
                          {cart.id}
                        </td>

                        <td className="px-7 py-5 text-[14px] text-[#5f6474]">
                          {fmtDateTime(cart.created_at)}
                        </td>

                        <td className="px-7 py-5">
                          <div className="flex flex-col">
                            <span className="text-[14px] font-semibold text-[#111322]">
                              {cart.contact_name || "--"}
                            </span>
                            <span className="text-[13px] text-[#7b8194]">
                              {cart.contact_identification || "Sin identificación"}
                            </span>
                          </div>
                        </td>

                        <td className="px-7 py-5 text-[14px] text-[#5f6474]">
                          {cart.contact_phone || "--"}
                        </td>

                        <td className="px-7 py-5 text-[14px] font-semibold text-[#111322]">
                          {peso(cart.total)}
                        </td>

                        <td className="px-7 py-5">
                          <BadgeRecovery cmp={cmp} amount={amount} />
                        </td>

                        <td className="px-7 py-5">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCart(cart);
                                setShowModal(true);
                              }}
                              className="w-10 h-10 rounded-xl bg-[#efeff5] hover:bg-[#e8e8f0] flex items-center justify-center transition"
                            >
                              <Eye className="w-4 h-4 text-[#2f3546]" />
                            </button>

                            <button
                              type="button"
                              onClick={() => removeCart(cart.id)}
                              className="w-10 h-10 rounded-xl bg-[#fff0f0] hover:bg-[#ffe4e4] flex items-center justify-center transition"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-7 py-12 text-center text-[#7b8194]">
                      No hay carritos para mostrar.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* BOTÓN AL FINAL DE LA TABLA */}
          {hasMore && initialCarts.length > 0 && (
            <div className="p-7 border-t border-[#e6e7ee] flex justify-center">
              <button
                type="button"
                onClick={fetchMoreCarts}
                disabled={loading}
                className="h-12 px-5 rounded-2xl bg-[#111322] text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {loading ? "Cargando..." : "Cargar más"}
              </button>
            </div>
          )}
        </DashboardCard>
      </div>

      {/* MODAL */}
      {showModal && selectedCart ? (
        <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-[28px] bg-[#f8f8fb] border border-[#e8e8ee] shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-[#e6e7ee] bg-[#f8f8fb]">
              <div>
                <h3 className="text-[24px] font-bold text-[#111322]">
                  Carrito #{selectedCart.id}
                </h3>
                <p className="text-sm text-[#7b8194] mt-1">
                  {selectedCart.contact_name || "--"} · {selectedCart.contact_phone || "--"}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-11 h-11 rounded-2xl bg-[#efeff5] flex items-center justify-center"
              >
                <X className="w-5 h-5 text-[#2f3546]" />
              </button>
            </div>

            <div className="p-6 flex flex-col gap-6">
              {/* RESUMEN */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <DashboardCard className="p-5">
                  <p className="text-xs uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">
                    Cliente
                  </p>
                  <p className="text-[18px] font-bold text-[#111322] mt-3">
                    {selectedCart.contact_name || "--"}
                  </p>
                  <p className="text-sm text-[#7b8194] mt-1">
                    {selectedCart.contact_phone || "--"}
                  </p>
                  <p className="text-sm text-[#7b8194] mt-1">
                    {selectedCart.contact_identification || "Sin identificación"}
                  </p>
                </DashboardCard>

                <DashboardCard className="p-5">
                  <p className="text-xs uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">
                    Ubicación
                  </p>
                  <p className="text-[18px] font-bold text-[#111322] mt-3">
                    {selectedCart.shipping_city || "—"}, {selectedCart.shipping_province || "—"}
                  </p>
                  <p className="text-sm text-[#7b8194] mt-1">
                    {selectedCart.shipping_address || selectedCart.address || "Sin dirección"}
                  </p>
                </DashboardCard>

                <DashboardCard className="p-5">
                  <p className="text-xs uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">
                    Total carrito
                  </p>
                  <p className="text-[18px] font-bold text-[#111322] mt-3">
                    {peso(selectedCart.total)}
                  </p>
                  <p className="text-sm text-[#7b8194] mt-1">
                    {fmtDateTime(selectedCart.created_at)}
                  </p>
                </DashboardCard>
              </div>

              {/* PRODUCTOS */}
              <DashboardCard className="p-6">
                <SectionTitle title="Productos del carrito" subtitle="DETALLE DE PRODUCTOS" />
                <div className="overflow-x-auto mt-6">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-[#e6e7ee]">
                        <th className="py-4 pr-4 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">Producto</th>
                        <th className="py-4 pr-4 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">SKU</th>
                        <th className="py-4 pr-4 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">Cantidad</th>
                        <th className="py-4 pr-4 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">Precio (u.)</th>
                        <th className="py-4 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCart.products?.map((p, i) => (
                        <tr key={i} className="border-b border-[#efeff4]">
                          <td className="py-4 pr-4 text-[#111322]">{p.name}</td>
                          <td className="py-4 pr-4 text-[#5f6474]">{p.sku}</td>
                          <td className="py-4 pr-4 text-[#5f6474]">{p.quantity}</td>
                          <td className="py-4 pr-4 text-[#5f6474]">{peso(toNum(p.price))}</td>
                          <td className="py-4 text-[#111322] font-semibold">
                            {peso(toNum(p.quantity) * toNum(p.price))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </DashboardCard>

              {/* VENTAS RECUPERADAS */}
              <DashboardCard className="p-6">
                <SectionTitle
                  title="Ventas recuperadas"
                  subtitle={`DENTRO DE ${attributionDays} DÍAS`}
                />

                <div className="mt-6">
                  {(() => {
                    const ventas = Array.isArray(selectedCart.recoveredVentas)
                      ? selectedCart.recoveredVentas
                      : [];

                    if (!ventas.length) {
                      return (
                        <p className="text-sm text-[#7b8194]">
                          Sin ventas recuperadas dentro de {attributionDays} días.
                        </p>
                      );
                    }

                    const cartMs = selectedCart._createdMs || parseMs(selectedCart.created_at);

                    const totalAtribuido =
                      toNum(
                        selectedCart.recoveredAmountConEnvio ||
                          selectedCart.recoveredAmountProducts
                      ) ||
                      ventas.reduce(
                        (s, v) => s + (toNum(v.total_paid) || toNum(v.total)),
                        0
                      );

                    const cmpText = (() => {
                      const cmp = selectedCart.recoveredCmp;
                      if (cmp === "eq") return "Igual";
                      if (cmp === "gt") return "Mayor";
                      if (cmp === "lt") return "Menor";

                      return totalAtribuido === toNum(selectedCart.total)
                        ? "Igual"
                        : totalAtribuido > toNum(selectedCart.total)
                        ? "Mayor"
                        : "Menor";
                    })();

                    return (
                      <div className="space-y-5">
                        {ventas.map((v) => {
                          const vMs = v.createdAtMs ?? parseMs(v.createdAt);
                          const total = toNum(v.total_paid) || toNum(v.total);
                          const diffMs = vMs - cartMs;
                          const hours = Math.max(0, Math.floor(diffMs / 3600000));
                          const minutes = Math.max(
                            0,
                            Math.floor((diffMs % 3600000) / 60000)
                          );

                          return (
                            <div
                              key={v.id}
                              className="rounded-2xl border border-[#e6e7ee] bg-white p-5"
                            >
                              <div className="flex flex-wrap items-center gap-3">
                                <span className="text-[16px] font-bold text-[#111322]">
                                  #{v.number || v.id}
                                </span>
                                <span className="text-[14px] text-[#7b8194]">
                                  {fmtDateTime(vMs)}
                                </span>
                                <span className="text-[15px] font-semibold text-[#111322]">
                                  {peso(total)}
                                </span>
                                <span className="text-[14px] text-[#7b8194]">
                                  ⏱ {hours}h {minutes}m
                                </span>
                              </div>

                              {Array.isArray(v.products) && v.products.length > 0 ? (
                                <div className="overflow-x-auto mt-5">
                                  <table className="min-w-full">
                                    <thead>
                                      <tr className="border-b border-[#e6e7ee]">
                                        <th className="py-3 pr-4 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">Producto</th>
                                        <th className="py-3 pr-4 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">SKU</th>
                                        <th className="py-3 pr-4 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">Cant.</th>
                                        <th className="py-3 pr-4 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">Precio</th>
                                        <th className="py-3 text-left text-[12px] uppercase tracking-[0.1em] text-[#8a8fa1] font-semibold">Subtotal</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {v.products.map((p, i) => (
                                        <tr key={i} className="border-b border-[#efeff4]">
                                          <td className="py-3 pr-4 text-[#111322]">{p.name}</td>
                                          <td className="py-3 pr-4 text-[#5f6474]">{p.sku}</td>
                                          <td className="py-3 pr-4 text-[#5f6474]">{p.quantity}</td>
                                          <td className="py-3 pr-4 text-[#5f6474]">{peso(toNum(p.price))}</td>
                                          <td className="py-3 text-[#111322] font-semibold">
                                            {peso(
                                              toNum(p.subtotal) ||
                                                toNum(p.quantity) * toNum(p.price)
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}

                        <div className="rounded-2xl bg-[#fff4cc] p-5">
                          <p className="text-[16px] font-bold text-[#111322]">
                            Total atribuido: {peso(totalAtribuido)}
                          </p>
                          <p className="text-sm text-[#7b8194] mt-1">
                            Comparación vs carrito: {cmpText}
                          </p>

                          {selectedCart.recoveredProductComparison ? (
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <div className="font-semibold text-[#111322] mb-1">
                                  Coincidencias por SKU
                                </div>
                                <div className="text-[#7b8194]">
                                  {(selectedCart.recoveredProductComparison.bySku || []).join(", ") || "—"}
                                </div>
                              </div>

                              <div>
                                <div className="font-semibold text-[#111322] mb-1">
                                  Coincidencias por Nombre
                                </div>
                                <div className="text-[#7b8194]">
                                  {(selectedCart.recoveredProductComparison.byName || []).join(", ") || "—"}
                                </div>
                              </div>

                              <div>
                                <div className="font-semibold text-[#111322] mb-1">
                                  Solo en carrito (SKU)
                                </div>
                                <div className="text-[#7b8194]">
                                  {(selectedCart.recoveredProductComparison.onlyInCartBySku || []).join(", ") || "—"}
                                </div>
                              </div>

                              <div>
                                <div className="font-semibold text-[#111322] mb-1">
                                  Solo en ventas (SKU)
                                </div>
                                <div className="text-[#7b8194]">
                                  {(selectedCart.recoveredProductComparison.onlyInSalesBySku || []).join(", ") || "—"}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </DashboardCard>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}